import { Button, List, ListItem, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import debounce from 'lodash/debounce';
import React, { useCallback, useMemo, useState } from 'react';
import * as API from '../../API';
import type Department from '../../Models/Department';
import type Patient from '../../Models/Patient';
import type Physician from '../../Models/Physician';
import type Prescription from '../../Models/Prescription';
import type PrescriptionCheck from '../../Models/PrescriptionCheck';
import type PrescriptionPreviousExam from '../../Models/PrescriptionPreviousExam';
import type PrescriptionProcedure from '../../Models/PrescriptionProcedure';
import type Procedure from '../../Models/Procedure';
import type SelectListItem from '../../Models/SelectListItem';
import Styles from './CreatePrescriptions.module.scss';

function CreatePrescriptions(): React.ReactElement {
    const [searchedPhysicians, setSearchedPhysicians] = useState<Physician[]>([]);
    const [searchedDepartments, setSearchedDepartments] = useState<SelectListItem[]>([]);
    const [searchedPatients, setSearchedPatients] = useState<Patient[]>([]);
    const [searchedProcedures, setSearchedProcedures] = useState<Procedure[]>([]);
    const [selectedPatient, setSelectedPatient] = useState<Patient>();
    const [selectedDepartmentID, setSelectedDepartmentID] = useState<number>(0);
    const [procedures, setProcedures] = useState<PrescriptionProcedure[]>([]);
    const [selectedPhysician, setSelectedPhysician] = useState<Physician>();
    const [selectedProcedure, setSelectedProcedure] = useState<Procedure>();
    const [clinicalInfo, setClinicalInfo] = useState<string>('');
    const [diagnosticQuestion, setDiagnosticQuestion] = useState<string>('');
    const [checkToAdd, setCheckToAdd] = useState<string>('');
    const [previousExamToAdd, setPreviousExamToAdd] = useState<string>('');
    const [checks, setChecks] = useState<PrescriptionCheck[]>([]);
    const [previousExams, setPreviousExams] = useState<PrescriptionPreviousExam[]>([]);

    const searchPhysician = useCallback((e: any, searchTerm: string): void => {
        if (searchTerm === '') {
            return;
        }

        // search for nomenclatures
        API.getPhysicianForTerm(searchTerm)
            .then((x) => {
                setSearchedPhysicians(x.physicians);
            })
            .catch(() => {
                console.error('Something went wrong while getting physicians for term');
            });
    }, []);

    const searchDepartment = useCallback((e: any, searchTerm: string): void => {
        if (searchTerm === '') {
            return;
        }

        // search for nomenclatures
        API.getDepartmentForTerm(searchTerm)
            .then((x) => {
                setSearchedDepartments(x);
            })
            .catch(() => {
                console.error('Something went wrong while getting departments for term');
            });
    }, []);

    const searchPatient = useCallback((e: any, searchTerm: string): void => {
        if (searchTerm === '') {
            return;
        }

        // search for nomenclatures
        API.getPatientsForTerm(searchTerm)
            .then((x) => {
                setSearchedPatients(x);
            })
            .catch(() => {
                console.error('Something went wrong while getting patients for term');
            });
    }, []);

    const searchProcedure = useCallback((e: any, searchTerm: string): void => {
        if (searchTerm === '') {
            return;
        }

        // search for nomenclatures
        if (selectedPatient == null) {
            throw new Error('Cannot search for procedures because no patient is selected.');
        }
        API.getProceduresForTerm(searchTerm, selectedPatient.ID)
            .then((x) => {
                setSearchedProcedures(x);
            })
            .catch(() => {
                console.error('Something went wrong while getting procedures for term');
            });
    }, [selectedPatient]);

    const debouncedSearchPatient = useMemo(() => debounce(searchPatient, 500), [searchPatient]);
    const debouncedSearchPhysician = useMemo(() => debounce(searchPhysician, 500), [searchPhysician]);
    const debouncedSearchDepartment = useMemo(() => debounce(searchDepartment, 500), [searchDepartment]);
    const debouncedSearchProcedure = useMemo(() => debounce(searchProcedure, 500), [searchProcedure]);

    const sendPrescription = (): void => {
        if (selectedPatient == null) {
            throw new Error('Cannot send the prescription because no patient is selected.');
        }
        if (selectedPhysician == null) {
            throw new Error('Cannot send the prescription because no physician is selected.');
        }

        const prescriptionModel: Prescription = {
            Clinicalinfo: clinicalInfo,
            Patient: selectedPatient,
            Physician: selectedPhysician,
            DiagnosticQuestion: diagnosticQuestion,
            RequestingDepartment: {
                ID: selectedDepartmentID,
            } satisfies Department,
            Procedures: procedures,
            Checks: checks,
            PreviousExams: previousExams,
        };
        API.sendPrescription(prescriptionModel)
            .then((res) => {
                if (!res.AllOk) {
                    return console.error('Issue with sendPrescription:', res);
                }
            })
            .catch((err) => {
                console.error('Could not unlink orders from invoicing batch:', err);
            });
    };

    const addCheck = (): void => {
        setChecks([...checks, { Name: checkToAdd, Value: '' }]);
    };

    const addPreviousExam = (): void => {
        setPreviousExams([...previousExams, { Name: previousExamToAdd, Checked: true }]);
    };

    const addProcedure = (): void => {
        if (selectedProcedure == null) {
            throw new Error('Cannot add a procedure because no procedure is selected.');
        }
        setProcedures([...procedures, { MappedProcedure: selectedProcedure }]);
    };

    const onPatientChange = (e: React.SyntheticEvent, value: Patient | null): void => {
        if (!value) {
            return;
        }
        setSelectedPatient(value);
    };

    const onPhysicianChange = (e: React.SyntheticEvent, value: Physician | null): void => {
        if (!value) {
            return;
        }
        setSelectedPhysician(value);
    };

    const onDepartmentChange = (e: React.SyntheticEvent, value: SelectListItem | null): void => {
        if (!value) {
            return;
        }
        setSelectedDepartmentID(Number(value.Value));
    };

    const onProcedureChange = (e: React.SyntheticEvent, value: Procedure | null): void => {
        if (!value) {
            return;
        }
        setSelectedProcedure(value);
    };

    return (
        <Grid container size={12} sx={{ all: 'initial' }} className={Styles.grid}>
            <Grid size={12} className={Styles.gridWidth} sx={{ all: 'initial' }}>
                <Autocomplete
                    className={Styles.searchBox}
                    onInputChange={debouncedSearchPatient}
                    options={searchedPatients}
                    onChange={onPatientChange}
                    getOptionLabel={(option) => `${option.FullName}`}
                    renderInput={(params) => <TextField {...params} label="Search Patient" />}
                />
            </Grid>
            <Grid size={12} className={Styles.gridWidth} sx={{ all: 'initial' }}>
                <Autocomplete
                    className={Styles.searchBox}
                    onInputChange={debouncedSearchPhysician}
                    options={searchedPhysicians}
                    getOptionLabel={(option) => `${option.FullName} - ${option.RIZIV}`}
                    renderInput={(params) => <TextField {...params} label="Search Physician" />}
                    onChange={onPhysicianChange}
                />
            </Grid>
            <Grid size={12} className={Styles.gridWidth} sx={{ all: 'initial' }}>
                <Autocomplete
                    className={Styles.searchBox}
                    onInputChange={debouncedSearchDepartment}
                    options={searchedDepartments}
                    getOptionLabel={(option) => `${option.Text}`}
                    renderInput={(params) => <TextField {...params} label="Search Department" />}
                    onChange={onDepartmentChange}
                />
            </Grid>
            <Grid size={12} className={`${Styles.gridWidth} ${Styles.procedureGrid}`} sx={{ all: 'initial' }}>
                <Autocomplete
                    className={Styles.searchBox}
                    onInputChange={debouncedSearchProcedure}
                    options={searchedProcedures}
                    getOptionLabel={(option) => `${option.Code} - ${option.FullCode}`}
                    renderInput={(params) => <TextField {...params} label="Search procedure" />}
                    onChange={onProcedureChange}
                />
                <Button className={`${Styles.prescriptionButton}`} onClick={addProcedure} variant="contained">
                    Add procedure
                </Button>

                <Typography>Procedures that will be added</Typography>
                <List>
                    {procedures.map((proc) => <ListItem key={proc.MappedProcedure.ID}> {proc.MappedProcedure.FullCode} </ListItem>)}
                </List>

            </Grid>
            <Grid size={12} className={Styles.gridWidth} sx={{ all: 'initial' }}>
                <TextField
                    className={Styles.textField}
                    label="Clinical info"
                    type="text"
                    value={clinicalInfo}
                    multiline
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setClinicalInfo(event.target.value);
                    }}
                />
            </Grid>
            <Grid size={12} className={Styles.gridWidth} sx={{ all: 'initial' }}>
                <TextField
                    className={Styles.textField}
                    label="Diagnostic Question"
                    type="text"
                    value={diagnosticQuestion}
                    multiline
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setDiagnosticQuestion(event.target.value);
                    }}
                />
            </Grid>
            <Grid size={12} className={Styles.gridWidth} sx={{ all: 'initial' }}>
                <TextField
                    className={Styles.textField}
                    label="Check"
                    type="text"
                    value={checkToAdd}
                    multiline
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setCheckToAdd(event.target.value);
                    }}
                />
                <Button className={`${Styles.prescriptionButton}`} onClick={addCheck} variant="contained">
                    Add check
                </Button>

                <Typography>Checks that will be added</Typography>
                <List>
                    {checks.map((check) => <ListItem key={check.Name}> {check.Name} </ListItem>)}
                </List>
            </Grid>
            <Grid size={12} className={Styles.gridWidth} sx={{ all: 'initial' }}>
                <TextField
                    className={Styles.textField}
                    label="Previous exam"
                    type="text"
                    value={previousExamToAdd}
                    multiline
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setPreviousExamToAdd(event.target.value);
                    }}
                />
                <Button className={`${Styles.prescriptionButton}`} onClick={addPreviousExam} variant="contained">
                    Add previous exam
                </Button>

                <Typography>Previous exams that will be added</Typography>
                <List>
                    {previousExams.map((exam) => <ListItem key={exam.Name}> {exam.Name} </ListItem>)}
                </List>
            </Grid>
            <Grid size={4} className={Styles.gridWidth} sx={{ all: 'initial' }}>
                <Button className={`${Styles.prescriptionButton}`} onClick={sendPrescription} variant="contained">
                    Add prescription!
                </Button>
            </Grid>

        </Grid>
    );
}

export default CreatePrescriptions;
